<template>
  <div class="space-y-6">
    <div class="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h2 class="text-lg font-bold text-neutral-900">用户权限管理</h2>
          <p class="text-sm text-neutral-500 mt-1">
            设置管理员的模块权限：工具审核、需求管理、常用链接等，可随时分配或收回
          </p>
        </div>
        <div class="flex gap-3">
          <div class="relative">
            <input
              type="text"
              v-model="searchKey"
              @input="handleSearch"
              placeholder="搜索昵称/用户名/手机号..."
              class="px-4 py-2 pl-10 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm w-64"
            />
            <i class="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"></i>
          </div>
          <button
            @click="refreshList"
            class="px-3 py-2 rounded-lg border border-neutral-300 hover:border-primary hover:text-primary transition-colors"
            title="刷新列表"
          >
            <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
          </button>
        </div>
      </div>

      <!-- 用户列表（两列并排） -->
      <div v-loading="loading" class="grid grid-cols-1 xl:grid-cols-2 gap-4" style="min-height: 120px">
        <div
          v-for="u in userList"
          :key="u.uid"
          class="bg-neutral-50 rounded-xl border border-neutral-200 p-4 sm:p-5 hover:shadow-md transition-shadow duration-200"
        >
          <div class="flex flex-col sm:flex-row sm:items-center gap-4">
            <!-- 用户信息 -->
            <div class="flex items-center gap-3 flex-1 min-w-0">
              <img
                v-if="u.avatar"
                :src="u.avatar"
                class="w-12 h-12 rounded-full object-cover border border-neutral-200"
                alt=""
              />
              <div
                v-else
                class="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xl font-bold"
              >
                {{ (u.name || '?').charAt(0) }}
              </div>
              <div class="min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="font-bold text-neutral-900">{{ u.name }}</span>
                  <span
                    class="text-xs px-2 py-0.5 rounded-full"
                    :class="{
                      'bg-purple-100 text-purple-700': u.adminPerms === '*',
                      'bg-blue-100 text-blue-700': u.utype === 'admin' && u.adminPerms !== '*',
                      'bg-neutral-100 text-neutral-500': u.utype !== 'admin',
                    }"
                  >
                    {{ roleText(u) }}
                  </span>
                </div>
                <div class="text-sm text-neutral-500 mt-1 flex flex-wrap gap-x-4">
                  <span v-if="u.loginId"><i class="fa fa-user-o mr-1"></i>{{ u.loginId }}</span>
                  <span v-if="u.phone"><i class="fa fa-phone mr-1"></i>{{ u.phone }}</span>
                  <span><i class="fa fa-clock-o mr-1"></i>{{ u.createTime }}</span>
                </div>
                <!-- 权限标签 -->
                <div v-if="u.utype === 'admin' && u.adminPerms !== '*'" class="mt-2 flex flex-wrap gap-1">
                  <span
                    v-for="p in permList(u.adminPerms)"
                    :key="p"
                    class="text-xs px-2 py-0.5 rounded bg-green-50 text-green-700 border border-green-200"
                  >
                    {{ permName(p) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- 操作 -->
            <div class="flex gap-2 shrink-0">
              <button
                @click="openEdit(u)"
                class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
              >
                <i class="fa fa-key mr-1"></i>设置权限
              </button>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-if="!loading && userList.length === 0" class="text-center py-12 text-neutral-400 col-span-full">
          <i class="fa fa-users text-4xl mb-3"></i>
          <p>暂无用户</p>
        </div>

        <!-- 分页 -->
        <div class="flex items-center justify-between pt-2 col-span-full" v-if="total > 0">
          <span class="text-sm text-neutral-500">共 {{ total }} 位用户</span>
          <div class="flex gap-2">
            <button
              :disabled="page <= 1"
              @click="changePage(page - 1)"
              class="px-3 py-1.5 rounded-lg border border-neutral-300 text-sm disabled:opacity-40 hover:border-primary hover:text-primary transition-colors"
            >
              上一页
            </button>
            <span class="px-3 py-1.5 text-sm text-neutral-600">{{ page }} / {{ totalPages }}</span>
            <button
              :disabled="page >= totalPages"
              @click="changePage(page + 1)"
              class="px-3 py-1.5 rounded-lg border border-neutral-300 text-sm disabled:opacity-40 hover:border-primary hover:text-primary transition-colors"
            >
              下一页
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 权限编辑弹窗 -->
    <el-dialog
      v-model="dialogVisible"
      :title="'设置权限 - ' + (editing ? editing.name : '')"
      width="480px"
    >
      <div v-if="editing" class="space-y-5">
        <div>
          <label class="text-sm font-medium text-neutral-700">角色</label>
          <div class="flex gap-3 mt-2">
            <label
              class="flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors"
              :class="form.utype === 'user' ? 'border-primary bg-primary/5 text-primary' : 'border-neutral-300'"
            >
              <input type="radio" value="user" v-model="form.utype" />
              普通用户
            </label>
            <label
              class="flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors"
              :class="form.utype === 'admin' && form.adminPerms !== '*' ? 'border-primary bg-primary/5 text-primary' : 'border-neutral-300'"
            >
              <input
                type="radio"
                value="admin"
                v-model="form.utype"
                @change="onRoleChange"
              />
              管理员
            </label>
            <label
              v-if="isSuper"
              class="flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors"
              :class="form.utype === 'admin' && form.adminPerms === '*' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-neutral-300'"
            >
              <input
                type="radio"
                value="super"
                v-model="superRadio"
                @change="onSuperChange"
              />
              超级管理员
            </label>
          </div>
        </div>

        <div v-if="form.utype === 'admin' && form.adminPerms !== '*'">
          <label class="text-sm font-medium text-neutral-700">可管理的模块</label>
          <div class="grid grid-cols-2 gap-2 mt-2">
            <label
              v-for="m in modules"
              :key="m.id"
              class="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors"
              :class="formPerms.includes(m.id) ? 'border-primary bg-primary/5 text-primary' : 'border-neutral-300 text-neutral-600'"
            >
              <input type="checkbox" :value="m.id" v-model="formPerms" />
              <i :class="'fa ' + m.icon"></i>
              {{ m.name }}
            </label>
          </div>
          <p class="text-xs text-neutral-400 mt-2">不勾选任何模块则仅能进入后台但无操作权限</p>
        </div>

        <div v-if="form.utype === 'user'" class="text-sm text-neutral-400 bg-neutral-50 rounded-lg p-3">
          取消管理员身份后，该用户将无法进入管理后台
        </div>
        <div v-if="form.adminPerms === '*' && form.utype === 'admin'" class="text-sm text-purple-600 bg-purple-50 rounded-lg p-3">
          超级管理员拥有全部模块权限，并可管理其他管理员的权限
        </div>
      </div>
      <template #footer>
        <button
          @click="dialogVisible = false"
          class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:border-neutral-400 transition-colors text-sm"
        >
          取消
        </button>
        <button
          @click="savePerm"
          :disabled="saving"
          class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium ml-2 disabled:opacity-50"
        >
          {{ saving ? '保存中...' : '保存' }}
        </button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage } from "element-plus";

export default {
  name: "UserPermManager",
  props: {
    // 当前操作者的权限串（* 为超管）
    myPerms: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      userList: [],
      total: 0,
      page: 1,
      pageSize: 10,
      searchKey: "",
      loading: false,
      dialogVisible: false,
      saving: false,
      editing: null,
      form: {
        utype: "user",
        adminPerms: "",
      },
      formPerms: [],
      superRadio: "",
      modules: [
        { id: "dashboard", name: "数据中心", icon: "fa-bar-chart" },
        { id: "uploads", name: "上传管理", icon: "fa-cloud-upload" },
        { id: "requests", name: "需求管理", icon: "fa-lightbulb-o" },
        { id: "points", name: "创值榜", icon: "fa-trophy" },
        { id: "links", name: "常用链接", icon: "fa-link" },
        { id: "banner", name: "首页轮播", icon: "fa-image" },
        { id: "category", name: "分类与学科", icon: "fa-sitemap" },
        { id: "webinfo", name: "网页YI起建", icon: "fa-window-maximize" },
        { id: "users", name: "用户管理", icon: "fa-users" },
        { id: "apply", name: "申请审核", icon: "fa-check-square-o" },
        { id: "prompt", name: "提示词审核", icon: "fa-comment-o" },
        { id: "agent", name: "智能体管理 / 审核", icon: "fa-robot" },
        { id: "software", name: "软件分享 / 审核", icon: "fa-cube" },
        { id: "cases", name: "案例审核", icon: "fa-graduation-cap" },
        { id: "home", name: "首页与板块", icon: "fa-th-large" },
        { id: "sso", name: "免登站点", icon: "fa-key" },
        { id: "kbedit", name: "知识体系编辑", icon: "fa-sitemap" },
      ],
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
    isSuper() {
      return this.myPerms === "*";
    },
  },
  created() {
    this.getList();
  },
  methods: {
    permName(id) {
      const m = this.modules.find((x) => x.id === id);
      return m ? m.name : id;
    },
    permList(perms) {
      return (perms || "").split(",").filter((x) => x && x !== "*");
    },
    roleText(u) {
      if (u.utype === "admin") {
        return u.adminPerms === "*" ? "超级管理员" : "管理员";
      }
      return "普通用户";
    },
    async getList() {
      this.loading = true;
      try {
        const data = await this.$aesEncrypt({
          page: this.page,
          pageSize: this.pageSize,
          searchKey: this.searchKey,
        });
        const res = await this.$axios({
          url: "user/admin/userlist",
          method: "POST",
          data: { data },
        });
        if (res.code === 200) {
          this.userList = res.data.list || [];
          this.total = res.data.total || 0;
        }
      } catch (e) {
        console.error("获取用户列表失败:", e);
      } finally {
        this.loading = false;
      }
    },
    refreshList() {
      this.page = 1;
      this.getList();
    },
    handleSearch() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.page = 1;
        this.getList();
      }, 400);
    },
    changePage(p) {
      this.page = p;
      this.getList();
    },
    openEdit(u) {
      this.editing = u;
      this.form.utype = u.utype === "admin" ? "admin" : "user";
      this.form.adminPerms = u.adminPerms || "";
      this.formPerms = this.permList(u.adminPerms);
      this.superRadio = u.adminPerms === "*" ? "super" : "";
      this.dialogVisible = true;
    },
    onRoleChange() {
      // 切到普通管理员时清空超管标记
      if (this.form.adminPerms === "*") {
        this.form.adminPerms = "";
        this.superRadio = "";
      }
    },
    onSuperChange() {
      if (this.superRadio === "super") {
        this.form.utype = "admin";
        this.form.adminPerms = "*";
      }
    },
    async savePerm() {
      if (!this.editing) return;

      // 超管不能取消自己的超管身份（防止系统失去管理者）
      if (this.editing.adminPerms === "*" && !(this.form.utype === "admin" && this.form.adminPerms === "*")) {
        ElMessage.warning("不能修改超级管理员自身的角色，请先为其他人设置超管权限");
        return;
      }

      let perms = this.form.adminPerms;
      if (this.form.utype === "admin" && perms !== "*") {
        perms = this.formPerms.join(",");
      }
      if (this.form.utype !== "admin") {
        perms = "";
      }

      this.saving = true;
      try {
        const data = await this.$aesEncrypt({
          uid: this.editing.uid,
          utype: this.form.utype,
          adminPerms: perms,
        });
        const res = await this.$axios({
          url: "user/admin/setperm",
          method: "POST",
          data: { data },
        });
        if (res.code === 200) {
          ElMessage.success("权限已更新");
          this.dialogVisible = false;
          this.getList();
        } else {
          ElMessage.error(res.msg || "保存失败");
        }
      } catch (e) {
        ElMessage.error("保存失败，请稍后重试");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
